import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import TurndownService from 'turndown';
import { motion, AnimatePresence } from 'framer-motion';
import { AudioRecorder } from '~/components/shared/AudioRecorder';
import { MediaLibrary } from './MediaLibrary';
import {
  XMarkIcon,
  PhotoIcon,
  DocumentPlusIcon,
  DocumentIcon,
  TagIcon,
  TableCellsIcon,
  ListBulletIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/solid';
import {
  MemoryEntry,
  MemoryMetadata,
  MemoryEntryContent,
  MediaContent,
} from '~/services/memory';
import 'react-quill/dist/quill.snow.css';

interface MediaItem {
  _id: string;
  name: string;
  url: string;
  mimetype: string;
  size: number;
  description: string;
}

interface MemoryEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  memory: MemoryEntry | null;
  onSave: (memory: MemoryEntry) => void;
  agentId: string;
  isNew?: boolean;
  submitToBackend?: boolean;
  isSaving?: boolean;
}

// Initialize Turndown service for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  fence: '```',
  emDelimiter: '*',
  strongDelimiter: '**',
  linkStyle: 'inlined',
  linkReferenceStyle: 'full',
});

// Configure turndown to handle Quill-specific HTML
turndownService.addRule('lineBreak', {
  filter: 'br',
  replacement: () => '\n',
});

turndownService.addRule('paragraph', {
  filter: 'p',
  replacement: (content) => (content ? content + '\n\n' : '\n'),
});

// Helper function to properly clean JSON strings with escaped characters
const cleanJsonString = (jsonStr: string): string => {
  return jsonStr
    .replace(/\\"/g, '"') // Fix escaped quotes
    .replace(/\\_/g, '_') // Fix escaped underscores
    .replace(/\\\\/g, '\\') // Fix double escaped backslashes
    .replace(/\\\//g, '/') // Fix escaped forward slashes
    .replace(/\\n/g, '\n') // Fix escaped newlines
    .replace(/\\r/g, '\r') // Fix escaped carriage returns
    .replace(/\\t/g, '\t') // Fix escaped tabs
    .replace(/\\+$/, ''); // Remove trailing backslashes
};

// Helper function to convert blob to base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix to get just the base64 string
      const base64Data = result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Utility functions for format conversion
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return '';

  // First, extract and temporarily replace media references to avoid double processing
  const mediaReferences: { placeholder: string; original: string }[] = [];
  let processedMarkdown = markdown;

  // Extract server media format: [media:{...}]
  const serverMediaRegex = /\[media:(.*?)\]/g;
  let match;
  let mediaIndex = 0;

  while ((match = serverMediaRegex.exec(markdown)) !== null) {
    const placeholder = `<!--MEDIA_PLACEHOLDER_${mediaIndex}-->`;
    mediaReferences.push({
      placeholder,
      original: match[0],
    });
    processedMarkdown = processedMarkdown.replace(match[0], placeholder);
    mediaIndex++;
  }

  // Basic markdown to HTML conversion with better whitespace handling
  let html = processedMarkdown
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^- (.*$)/gim, '<ul><li>$1</li></ul>')
    .replace(/^\d+\. (.*$)/gim, '<ol><li>$1</li></ol>')
    .replace(/<\/ul>\s*<ul>/g, '')
    .replace(/<\/ol>\s*<ol>/g, '')
    .split('\n\n')
    .map((paragraph) => (paragraph.trim() ? `<p>${paragraph}</p>` : ''))
    .join('');

  // Restore media references as visual components
  mediaReferences.forEach(({ placeholder, original }) => {
    try {
      const mediaMatch = original.match(/\[media:(.*?)\]/);
      if (mediaMatch) {
        // Clean up all escaped characters in the JSON string
        const cleanedJsonString = cleanJsonString(mediaMatch[1]);
        console.log(
          '✅ Successfully cleaned and parsing JSON for media:',
          cleanedJsonString.substring(0, 50) + '...',
        );
        const mediaData = JSON.parse(cleanedJsonString);
        console.log('✅ Successfully parsed media data:', {
          _id: mediaData._id,
          name: mediaData.name,
          mimetype: mediaData.mimetype,
        });
        const isImage = mediaData.mimetype?.startsWith('image/');
        const mediaUrl = `${import.meta.env.VITE_API_URL}/media/${
          mediaData.url
        }`;

        const thumbnailHtml = isImage
          ? `<div class="media-thumbnail">
              <img src="${mediaUrl}" alt="${
                mediaData.description || mediaData.name
              }">
              <div class="media-description">${
                mediaData.description || mediaData.name
              }</div>
            </div>`
          : `<div class="media-thumbnail">
              <div class="video-placeholder">
                📹 Video
              </div>
              <div class="media-description">${
                mediaData.description || mediaData.name
              }</div>
            </div>`;

        console.log('🎨 Generated thumbnail HTML:', thumbnailHtml);
        console.log('🔄 Replacing placeholder:', placeholder);

        html = html.replace(placeholder, thumbnailHtml);
        console.log(
          '✅ HTML after replacement contains media-thumbnail:',
          html.includes('media-thumbnail'),
        );
      }
    } catch (e) {
      // If parsing fails, just replace with the original
      console.error('Failed to parse media in markdownToHtml:', e);
      html = html.replace(placeholder, original);
    }
  });

  return html;
};

const htmlToMarkdown = (html: string, mediaRefs: Map<string, any>): string => {
  if (!html) return '';

  // First extract media thumbnails and convert them back to media format
  let markdown = html;

  // Handle our custom media thumbnails
  const mediaThumbnailRegex = /<div class="media-thumbnail"[^>]*>.*?<\/div>/gs;
  markdown = markdown.replace(mediaThumbnailRegex, (match) => {
    // Try to extract image src or video info from the thumbnail
    const imgMatch = match.match(/src="([^"]+)"/);
    const descMatch = match.match(
      /<div class="media-description">([^<]+)<\/div>/,
    );

    if (imgMatch) {
      const src = imgMatch[1];
      const urlMatch = src.match(/\/media\/([^?]+)/);
      if (urlMatch) {
        const filename = urlMatch[1];
        const mediaData = mediaRefs.get(filename);

        if (mediaData) {
          // Use the stored media reference to recreate the server format
          return `[media:${JSON.stringify(mediaData)}]`;
        } else {
          // Fallback to markdown format
          const description = descMatch ? descMatch[1].trim() : 'Media';
          return `![${description}](${src})`;
        }
      }
    } else if (match.includes('video-placeholder')) {
      // This is a video thumbnail
      const description = descMatch ? descMatch[1].trim() : 'Video';
      // Try to find the media data by description
      for (const [filename, mediaData] of mediaRefs.entries()) {
        if (
          mediaData.description === description ||
          mediaData.name === description
        ) {
          return `[media:${JSON.stringify(mediaData)}]`;
        }
      }
      return `[Video: ${description}]`;
    }

    return '[Media file]';
  });

  // Handle regular images (from Quill embeds)
  const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/g;
  markdown = markdown.replace(imgRegex, (match, src) => {
    // Extract filename from URL
    const urlMatch = src.match(/\/media\/([^?]+)/);
    if (urlMatch) {
      const filename = urlMatch[1];
      const mediaData = mediaRefs.get(filename);

      if (mediaData) {
        // Use the stored media reference
        return `[media:${JSON.stringify(mediaData)}]`;
      }
    }

    // Fallback to markdown image format
    const altMatch = match.match(/alt="([^"]*)"/);
    const alt = altMatch ? altMatch[1] : 'Image';
    return `![${alt}](${src})`;
  });

  // Handle video placeholders
  markdown = markdown.replace(
    /\[📹 Video: ([^\]]+)\]/g,
    (match, description) => {
      // Try to find the media data by description
      for (const [filename, mediaData] of mediaRefs.entries()) {
        if (
          mediaData.description === description ||
          mediaData.name === description
        ) {
          return `[media:${JSON.stringify(mediaData)}]`;
        }
      }
      return `[Video: ${description}]`;
    },
  );

  // Use turndown to convert HTML to markdown
  markdown = turndownService.turndown(markdown);

  // Clean up extra whitespace and normalize line breaks
  markdown = markdown
    .replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines
    .replace(/[ \t]+$/gm, '') // Remove trailing spaces
    .replace(/^[ \t]+/gm, '') // Remove leading spaces (except for code blocks)
    .trim();

  return markdown;
};

// Utility function to strip __typename fields from objects
const stripTypenameFields = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(stripTypenameFields);
  }

  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key !== '__typename') {
      cleaned[key] = stripTypenameFields(value);
    }
  }
  return cleaned;
};

export const MemoryEditorDialog = ({
  isOpen,
  onClose,
  memory,
  onSave,
  agentId,
  isNew = false,
  submitToBackend = false,
  isSaving: externalIsSaving = false,
}: MemoryEditorDialogProps) => {
  const [t] = useTranslation('memory_editor');
  const [tTraining] = useTranslation('training_settings');
  const [content, setContent] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<MediaContent | null>(null);
  const [metadata, setMetadata] = useState<MemoryMetadata>({
    tags: [],
    source: 'manual',
    source_id: '',
    source_name: 'Manual Entry',
    source_type: 'document',
    source_url: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const quillRef = useRef<ReactQuill>(null);

  // Enhanced state to track media references
  const [mediaReferences, setMediaReferences] = useState<Map<string, any>>(
    new Map(),
  );

  // Use external saving state if provided, otherwise use local state
  const isCurrentlySaving = externalIsSaving || isSaving;

  // Simple content change handler - no conversion during typing
  const handleContentChange = useCallback((value: string) => {
    setContent(value);
  }, []);

  // Stable key for ReactQuill - only changes when memory changes, not content
  const quillKey = `quill-${memory?._id || 'new'}-${isOpen}`;

  // Enhanced Quill modules configuration with stable reference
  const stableModules = useMemo(
    () => ({
      toolbar: false,
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: function () {
              return true;
            },
          },
        },
      },
    }),
    [],
  );

  // Stable formats array
  const stableFormats = useMemo(
    () => [
      'header',
      'font',
      'size',
      'bold',
      'italic',
      'underline',
      'strike',
      'blockquote',
      'list',
      'bullet',
      'indent',
      'link',
      'image',
      'video',
      'color',
      'background',
      'align',
      'code-block',
    ],
    [],
  );

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setContent('');
      setMediaReferences(new Map());
      setMetadata({
        tags: [],
        source: 'manual',
        source_id: '',
        source_name: 'Manual Entry',
        source_type: 'document',
        source_url: '',
      });
      setIsSaving(false);
      setNewTag('');
      setSelectedText('');
      setCursorPosition(0);
      setIsSidebarOpen(false);
      setShowMediaLibrary(false);
    }
  }, [isOpen]);

  // Initialize content when memory changes
  useEffect(() => {
    if (memory && isOpen) {
      // Handle multimodal content structure
      if (memory.content && typeof memory.content === 'object') {
        // New multimodal structure
        const { text, media } = memory.content;

        // Set text content
        setContent(text || '');

        // Set single media item (take first one if multiple exist)
        // Strip __typename fields from Apollo Client cache
        if (media && media.length > 0) {
          setSelectedMedia(stripTypenameFields(media[0]));
        } else {
          setSelectedMedia(null);
        }
      } else if (typeof memory.content === 'string') {
        // Legacy string content - convert markdown to HTML for display
        const markdownContent = memory.content;
        const htmlContent = markdownToHtml(markdownContent);
        setContent(htmlContent);
        setSelectedMedia(null);

        // Extract and store media references for preservation
        const mediaRefs = new Map();
        const serverMediaRegex = /\[media:(.*?)\]/g;
        let match;
        while ((match = serverMediaRegex.exec(markdownContent)) !== null) {
          try {
            // Clean up all escaped characters in the JSON string
            console.log('🔍 Raw JSON reference:', match[1]);
            const cleanedJsonString = cleanJsonString(match[1]);
            console.log('🧹 Cleaned JSON reference:', cleanedJsonString);
            const mediaData = JSON.parse(cleanedJsonString);
            mediaRefs.set(mediaData.url, mediaData);
          } catch (e) {
            console.error('Failed to parse media reference:', e);
          }
        }
        setMediaReferences(mediaRefs);
      }

      // Set metadata
      setMetadata(
        memory.metadata || {
          tags: [],
          source: 'manual',
          source_id: '',
          source_name: 'Manual Entry',
          source_type: 'document',
          source_url: '',
        },
      );
    } else if (isOpen && !memory) {
      // New memory - reset to defaults
      setContent('');
      setSelectedMedia(null);
      setMediaReferences(new Map());
      setMetadata({
        tags: [],
        source: 'manual',
        source_id: '',
        source_name: 'Manual Entry',
        source_type: 'document',
        source_url: '',
      });
    }
  }, [memory, isOpen]);

  // Handle cursor position and selection changes
  const handleSelectionChange = useCallback((range: any) => {
    if (range) {
      setCursorPosition(range.index);
      if (range.length > 0) {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const selectedContent = quill.getText(range.index, range.length);
          setSelectedText(selectedContent.trim());
        }
      } else {
        setSelectedText('');
      }
    }
  }, []);

  // Audio transcription handler
  const handleAudioTranscription = useCallback(
    (transcription: string) => {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        quill.insertText(cursorPosition, transcription + ' ');
        quill.setSelection(cursorPosition + transcription.length + 1, 0);
      }
    },
    [cursorPosition],
  );

  // Media handlers - updated to work with single media item
  const handleMediaUploadComplete = async (media: any) => {
    try {
      setError(''); // Clear any existing errors
      let description = media.description || media.name;

      // Always auto-generate description for images
      if (media.mimetype?.startsWith('image/')) {
        try {
          // Fetch the image and convert to base64
          const imageUrl = `${import.meta.env.VITE_API_URL}/media/${media.url}`;
          const imageResponse = await fetch(imageUrl);
          const imageBlob = await imageResponse.blob();
          const base64 = await blobToBase64(imageBlob);

          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/files/describe-image`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
              },
              body: JSON.stringify({
                image: base64,
                agent_id: agentId,
              }),
            },
          );

          if (response.ok) {
            const result = await response.json();
            const aiDescription = result.data?.description;
            if (aiDescription) {
              // Combine AI description with user description if both exist
              description =
                description && description !== media.name
                  ? `${aiDescription}. ${description}`
                  : aiDescription;
            }
          }
        } catch (error) {
          console.error('Failed to generate AI description:', error);
        }
      }

      // Create media content object
      const mediaContent: MediaContent = {
        type: media.mimetype.startsWith('image/')
          ? 'image'
          : media.mimetype.startsWith('video/')
            ? 'video'
            : media.mimetype.startsWith('audio/')
              ? 'audio'
              : 'document',
        url: media.url,
        metadata: {
          _id: media._id,
          name: media.name,
          mimetype: media.mimetype,
          size: media.size,
          description: description,
          agent_id: media.agent_id,
          owner_id: media.owner_id,
        },
      };

      setSelectedMedia(mediaContent);
    } catch (error) {
      console.error('Error processing uploaded media:', error);
    }
  };

  const handleMediaSelect = (media: MediaItem) => {
    // Create media content object from library selection
    const mediaContent: MediaContent = {
      type: media.mimetype.startsWith('image/')
        ? 'image'
        : media.mimetype.startsWith('video/')
          ? 'video'
          : media.mimetype.startsWith('audio/')
            ? 'audio'
            : 'document',
      url: media.url,
      metadata: {
        _id: media._id,
        name: media.name,
        mimetype: media.mimetype,
        size: media.size,
        description: media.description || media.name,
        agent_id: agentId,
        owner_id: agentId,
      },
    };

    setSelectedMedia(mediaContent);
  };

  const handleRemoveMedia = () => {
    setSelectedMedia(null);
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0]; // Only handle the first file since we allow single media
    await handleFileUpload(file);
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    // Client-side size limit (configurable via env, defaults to 150MB)
    const maxMb = Number(import.meta.env.VITE_MAX_MEDIA_UPLOAD_MB || '150');
    const maxBytes = maxMb * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File is too large. Maximum allowed size is ${maxMb} MB.`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('agent_id', agentId);

      // Use XMLHttpRequest to get upload progress events
      await new Promise<void>((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${import.meta.env.VITE_API_URL}/files/upload`);
        const token = localStorage.getItem('accessToken') || '';
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percent);
          }
        };

        xhr.onreadystatechange = async () => {
          if (xhr.readyState === 4) {
            try {
              if (xhr.status >= 200 && xhr.status < 300) {
                const result = JSON.parse(xhr.responseText);
                await handleMediaUploadComplete(result.data);
              } else {
                const errorData = (() => {
                  try {
                    return JSON.parse(xhr.responseText);
                  } catch {
                    return {} as any;
                  }
                })();
                setError(
                  errorData.message ||
                    `Failed to upload file (status ${xhr.status})`,
                );
              }
            } finally {
              resolve();
            }
          }
        };

        xhr.onerror = () => {
          setError('Failed to upload file. Please try again.');
          resolve();
        };

        xhr.send(formData);
      });
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !metadata.tags.includes(newTag.trim())) {
      setMetadata({
        ...metadata,
        tags: [...metadata.tags, newTag.trim()],
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setMetadata({
      ...metadata,
      tags: metadata.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleMetadataChange = (field: keyof MemoryMetadata, value: string) => {
    setMetadata({
      ...metadata,
      [field]: value,
    });
  };

  const handleSave = () => {
    // Only set local saving state if not using external state
    if (!externalIsSaving) {
      setIsSaving(true);
    }
    setError(''); // Clear any existing errors

    // Extract text content from the editor
    const quill = quillRef.current?.getEditor();
    const textContent = quill?.getText()?.trim() || '';

    // Create multimodal content structure
    // Strip __typename fields before sending to GraphQL
    const multimodalContent: MemoryEntryContent = {
      text: textContent || undefined,
      media: selectedMedia ? [stripTypenameFields(selectedMedia)] : undefined,
    };

    // Ensure at least one content type is present
    if (!multimodalContent.text && !multimodalContent.media?.length) {
      setError(t('content_required', 'Please add some text or media content.'));
      if (!externalIsSaving) {
        setIsSaving(false);
      }
      return;
    }

    const finalMetadata = {
      ...metadata,
      source_id: metadata.source_id || `manual_${Date.now()}`,
    };

    const updatedMemory: MemoryEntry = {
      _id: memory?._id || `temp_${Date.now().toString()}`,
      agent_id: agentId,
      content: multimodalContent,
      created_at: memory?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      embedding: memory?.embedding || [],
      metadata: finalMetadata,
    };

    // Call the save handler - this will trigger the mutation
    onSave(updatedMemory);

    // Note: When using external saving state, the parent component will handle
    // resetting the state and closing the dialog after the mutation completes
  };

  const insertList = (type: 'ordered' | 'bullet') => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      quill.format('list', type);
    }
  };

  const toggleFormat = (format: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const currentFormat = quill.getFormat();
      quill.format(format, !currentFormat[format]);
    }
  };

  // Sidebar animation variants
  const sidebarVariants = {
    closed: {
      width: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
    open: {
      width: 320,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  if (!isOpen) {
    return null;
  }

  // Get character count from current HTML content (converted to text for accurate count)
  const textContent = content
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const characterCount = textContent.length;
  const wordCount = textContent
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  return (
    <div className="bg-base-100 fixed inset-0 z-50">
      {/* Custom styles to ensure text visibility */}
      <style>{`
        .ql-editor {
          color: #1f2937 !important;
          font-size: 16px !important;
          line-height: 1.6 !important;
          background: white !important;
          min-height: 400px !important;
          padding: 16px !important;
        }
        .ql-editor p {
          color: #1f2937 !important;
          margin: 0 0 16px 0 !important;
          font-size: 16px !important;
        }
        .ql-editor * {
          color: #1f2937 !important;
        }
        .ql-container {
          border: none !important;
          font-size: 16px !important;
          background: white !important;
        }
        .ql-toolbar {
          display: none !important;
        }
        .ql-blank::before {
          color: #9ca3af !important;
          font-style: italic !important;
          font-size: 16px !important;
        }
        /* Force visibility of all Quill content */
        .ql-editor div, .ql-editor p, .ql-editor span {
          visibility: visible !important;
          opacity: 1 !important;
          display: block !important;
        }
        /* Media thumbnail styles */
        .media-thumbnail {
          display: inline-block !important;
          margin: 8px 0 !important;
          padding: 8px !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 8px !important;
          background: #f9fafb !important;
          max-width: 100% !important;
        }
        .media-thumbnail img {
          max-width: 200px !important;
          max-height: 150px !important;
          border-radius: 4px !important;
          display: block !important;
          object-fit: cover !important;
        }
        .media-thumbnail .video-placeholder {
          width: 200px !important;
          height: 150px !important;
          background: #374151 !important;
          border-radius: 4px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: white !important;
          font-size: 14px !important;
        }
        .media-thumbnail .media-description {
          margin-top: 4px !important;
          font-size: 12px !important;
          color: #6b7280 !important;
          line-height: 1.4 !important;
        }
        /* Ensure media thumbnails are properly visible in Quill */
        .ql-editor .media-thumbnail {
          color: #1f2937 !important;
        }
        .ql-editor .media-thumbnail * {
          color: inherit !important;
        }
      `}</style>

      {/* Header */}
      <div className="border-base-300 bg-base-100 flex h-16 items-center justify-between border-b px-4 shadow-sm">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            aria-label={t('close_editor', 'Close editor')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">
            {isNew
              ? tTraining('add_memory', 'Add Memory')
              : t('edit_memory', 'Edit Memory')}
          </h1>
          <div className="badge badge-info badge-sm text-gray-900">
            {t('markdown_format', 'Markdown Format')}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Audio Recorder */}
          <div
            className="tooltip tooltip-bottom"
            data-tip={t('voice_input', 'Voice input')}
          >
            <AudioRecorder
              onTranscriptionComplete={handleAudioTranscription}
              enablePulseAnimation={false}
            />
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="btn btn-primary btn-sm rounded-full"
            disabled={
              isCurrentlySaving || (!textContent.trim() && !selectedMedia)
            }
          >
            {isCurrentlySaving ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              tTraining('save', 'Save')
            )}
          </button>
        </div>
      </div>

      {/* Custom Toolbar */}
      <div
        id="toolbar"
        className="bg-base-50 border-base-300 flex flex-wrap items-center gap-2 border-b p-2"
      >
        {/* Text Formatting */}
        <div className="join">
          <button
            className="btn btn-sm join-item"
            onClick={() => toggleFormat('bold')}
            title={t('bold', 'Bold (Markdown: **text**)')}
          >
            <BoldIcon className="h-4 w-4" />
          </button>
          <button
            className="btn btn-sm join-item"
            onClick={() => toggleFormat('italic')}
            title={t('italic', 'Italic (Markdown: *text*)')}
          >
            <ItalicIcon className="h-4 w-4" />
          </button>
          <button
            className="btn btn-sm join-item"
            onClick={() => toggleFormat('underline')}
            title={t('underline', 'Underline')}
          >
            <UnderlineIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="divider divider-horizontal"></div>

        {/* Headers */}
        <select
          className="ql-header select select-sm"
          title="Headers (Markdown: # ## ###)"
        >
          <option value="">{t('normal', 'Normal')}</option>
          <option value="1">{t('heading_1', 'Heading 1')}</option>
          <option value="2">{t('heading_2', 'Heading 2')}</option>
          <option value="3">{t('heading_3', 'Heading 3')}</option>
        </select>

        <div className="divider divider-horizontal"></div>

        {/* Lists */}
        <div className="join">
          <button
            className="btn btn-sm join-item"
            onClick={() => insertList('bullet')}
            title={t('bullet_list', 'Bullet List (Markdown: - item)')}
          >
            <ListBulletIcon className="h-4 w-4" />
          </button>
          <button
            className="ql-list btn btn-sm join-item"
            value="ordered"
            title={t('numbered_list', 'Numbered List (Markdown: 1. item)')}
          >
            1.
          </button>
        </div>

        <div className="divider divider-horizontal"></div>

        {/* Media - Updated for single media approach */}
        <div className="dropdown">
          <div tabIndex={0} role="button" className="btn btn-sm">
            <PhotoIcon className="h-4 w-4" />
            {selectedMedia
              ? t('change_media', 'Change Media')
              : t('add_media', 'Add Media')}
            <ChevronRightIcon className="h-3 w-3" />
          </div>
          <ul
            tabIndex={0}
            className="menu dropdown-content rounded-box bg-base-100 z-[1] w-52 p-2 shadow"
          >
            <li>
              <a onClick={() => setShowMediaLibrary(true)}>
                <PhotoIcon className="h-4 w-4" />
                {t('media_library', 'Media Library')}
              </a>
            </li>
            {selectedMedia && (
              <li>
                <a onClick={handleRemoveMedia} className="text-red-600">
                  <XMarkIcon className="h-4 w-4" />
                  {t('remove_media', 'Remove Media')}
                </a>
              </li>
            )}
          </ul>
        </div>

        <div className="divider divider-horizontal"></div>

        {/* Standard Quill Controls */}
        <button
          className="ql-blockquote btn btn-sm"
          title={t('quote', 'Quote')}
        ></button>
        <button
          className="ql-code-block btn btn-sm"
          title={t('code_block', 'Code Block')}
        ></button>
        <button
          className="ql-link btn btn-sm"
          title={t('link', 'Link')}
        ></button>

        <div className="divider divider-horizontal"></div>

        {/* Sidebar Toggle */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className={`btn btn-sm ${
            isSidebarOpen ? 'btn-primary' : 'btn-ghost'
          }`}
          title={t('toggle_metadata_panel', 'Toggle Metadata Panel')}
        >
          <Cog6ToothIcon className="h-4 w-4" />
          {isSidebarOpen
            ? t('hide_metadata', 'Hide Metadata')
            : t('show_metadata', 'Show Metadata')}
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex h-[calc(100vh-8rem)] overflow-hidden">
        {/* Editor */}
        <div className="flex flex-1 flex-col bg-gray-100">
          <div className="relative flex-1 overflow-auto">
            <div className="mx-auto max-w-4xl space-y-6 px-8 py-6">
              {/* Media Section */}
              <div className="rounded-lg bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="flex items-center text-lg font-medium text-gray-900">
                    <PhotoIcon className="mr-2 h-5 w-5" />
                    {t('media_content', 'Media Content')}
                    <span className="ml-2 text-sm text-gray-500">
                      (
                      {t(
                        'optional_single_media',
                        'Optional - Single media item',
                      )}
                      )
                    </span>
                  </h3>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="alert alert-error mb-4">
                    <span>{error}</span>
                    <button
                      onClick={() => setError('')}
                      className="btn btn-ghost btn-xs"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {selectedMedia ? (
                  /* Media Preview */
                  <div className="relative">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-start gap-4">
                        {/* Media Thumbnail/Preview */}
                        <div className="flex-shrink-0">
                          {selectedMedia.type === 'image' ? (
                            <img
                              src={`${import.meta.env.VITE_API_URL}/media/${
                                selectedMedia.url
                              }`}
                              alt={selectedMedia.metadata.description}
                              className="h-24 w-24 rounded-lg object-cover"
                            />
                          ) : selectedMedia.type === 'video' ? (
                            <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-blue-100">
                              <span className="text-2xl">🎥</span>
                            </div>
                          ) : selectedMedia.type === 'audio' ? (
                            <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-green-100">
                              <span className="text-2xl">🎵</span>
                            </div>
                          ) : (
                            <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-gray-100">
                              <DocumentIcon className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Media Info */}
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate font-medium text-gray-900">
                            {selectedMedia.metadata.name}
                          </h4>
                          <p className="mt-1 text-sm text-gray-600">
                            {selectedMedia.metadata.description}
                          </p>
                          <div className="mt-2 text-xs text-gray-500">
                            {selectedMedia.metadata.mimetype} •{' '}
                            {Math.round(selectedMedia.metadata.size / 1024)} KB
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={handleRemoveMedia}
                          className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50"
                        >
                          <XMarkIcon className="h-4 w-4" />
                          {t('remove', 'Remove')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Media Upload Area with Drag & Drop */
                  <div
                    className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                      isDragOver
                        ? 'bg-primary/10 border-primary'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {isUploading ? (
                      <div className="space-y-4">
                        {uploadProgress > 0 && uploadProgress < 100 ? (
                          <>
                            <progress
                              className="progress progress-primary w-full"
                              value={uploadProgress}
                              max={100}
                            ></progress>
                            <p className="text-center text-sm text-gray-600">
                              {t('uploading', 'Uploading')} {uploadProgress}%
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="loading loading-spinner loading-lg text-primary mx-auto"></div>
                            <p className="text-gray-600">
                              {t(
                                'uploading_media',
                                'Uploading and generating AI description...',
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <PhotoIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                        <p className="mb-4 text-gray-600">
                          {isDragOver
                            ? t('drop_file_here', 'Drop your file here')
                            : t(
                                'drag_drop_or_click',
                                'Drag & drop a file here, or click to select',
                              )}
                        </p>
                        <div className="flex justify-center gap-2">
                          <label className="btn btn-primary btn-sm cursor-pointer rounded-full">
                            <DocumentPlusIcon className="mr-2 h-4 w-4" />
                            {t('choose_file', 'Choose File')}
                            <input
                              type="file"
                              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                              onChange={handleFileInputChange}
                              className="hidden"
                            />
                          </label>
                          <button
                            onClick={() => setShowMediaLibrary(true)}
                            className="btn btn-outline btn-sm"
                          >
                            <PhotoIcon className="mr-2 h-4 w-4" />
                            {t('choose_from_library', 'Choose from Library')}
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          {t(
                            'media_formats',
                            'Supports images, videos, audio, and documents',
                          )}
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Text Editor Section */}
              <div className="rounded-lg bg-white p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="flex items-center text-lg font-medium text-gray-900">
                    <DocumentTextIcon className="mr-2 h-5 w-5" />
                    {t('text_content', 'Text Content')}
                    <span className="ml-2 text-sm text-gray-500">
                      (
                      {t(
                        'optional_text_description',
                        'Optional - Text description or content',
                      )}
                      )
                    </span>
                  </h3>
                </div>

                <div className="min-h-[400px]">
                  <ReactQuill
                    ref={quillRef}
                    key={quillKey}
                    theme="snow"
                    value={content}
                    onChange={handleContentChange}
                    onChangeSelection={handleSelectionChange}
                    modules={stableModules}
                    formats={stableFormats}
                    className="min-h-[400px]"
                    placeholder={t(
                      'text_content_placeholder',
                      'Add optional text content to accompany your media...',
                    )}
                    style={{
                      height: '400px',
                      minHeight: '400px',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Animated Sidebar */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              variants={sidebarVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="bg-base-50 border-base-300 overflow-hidden border-l"
            >
              <div className="w-80 overflow-y-auto">
                <div className="space-y-6 p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-base-content flex items-center text-sm font-medium">
                      <Cog6ToothIcon className="mr-2 h-4 w-4" />
                      {tTraining('metadata', 'Metadata')}
                    </h3>
                    <button
                      onClick={() => setIsSidebarOpen(false)}
                      className="btn btn-ghost btn-xs"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Content Info */}
                  <div className="space-y-2">
                    <h3 className="text-base-content text-sm font-medium">
                      {t('content_info', 'Content Info')}
                    </h3>
                    <div className="text-xs text-gray-600">
                      <p>
                        {t('characters', 'Characters')}: {characterCount}
                      </p>
                      <p>
                        {t('words', 'Words')}: {wordCount}
                      </p>
                      <p className="mt-1 text-xs text-blue-600">
                        {t('format_info', 'Stored as: Markdown')}
                      </p>
                    </div>
                  </div>

                  {/* Markdown Guide */}
                  <div className="space-y-2">
                    <h3 className="text-base-content text-sm font-medium">
                      {t('markdown_guide', 'Markdown Guide')}
                    </h3>
                    <div className="text-base-content/70 space-y-1 text-xs">
                      <p>
                        <code>**bold**</code> → <strong>bold</strong>
                      </p>
                      <p>
                        <code>*italic*</code> → <em>italic</em>
                      </p>
                      <p>
                        <code># Heading 1</code>
                      </p>
                      <p>
                        <code>## Heading 2</code>
                      </p>
                      <p>
                        <code>- Bullet list</code>
                      </p>
                      <p>
                        <code>1. Numbered list</code>
                      </p>
                    </div>
                  </div>

                  {/* Tags Section */}
                  <div>
                    <label className="text-base-content mb-2 block text-sm font-medium">
                      <TagIcon className="mr-1 inline h-4 w-4" />
                      {t('tags', 'Tags')}
                    </label>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1">
                        {metadata.tags.map((tag, index) => (
                          <div
                            key={index}
                            className="badge badge-primary gap-1 text-white"
                          >
                            {tag}
                            <button
                              onClick={() => handleRemoveTag(tag)}
                              className="btn btn-ghost btn-xs"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === 'Enter' && handleAddTag()
                          }
                          placeholder={t('add_tag_placeholder', 'Add a tag...')}
                          className="input-sm input flex-1"
                        />
                        <button
                          onClick={handleAddTag}
                          className="btn btn-primary btn-sm rounded-full"
                          disabled={!newTag.trim()}
                        >
                          {t('add', 'Add')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Source Information */}
                  <div className="space-y-4">
                    <div>
                      <label className="label-text label">
                        {t('source_name', 'Source Name')}
                      </label>
                      <input
                        type="text"
                        value={metadata.source_name}
                        onChange={(e) =>
                          handleMetadataChange('source_name', e.target.value)
                        }
                        className="input-sm input w-full"
                        placeholder={t(
                          'source_name_placeholder',
                          'e.g., Company Manual',
                        )}
                      />
                    </div>

                    <div>
                      <label className="label-text label">
                        {t('source_type', 'Source Type')}
                      </label>
                      <select
                        value={metadata.source_type}
                        onChange={(e) =>
                          handleMetadataChange('source_type', e.target.value)
                        }
                        className="select select-sm w-full"
                      >
                        <option value="document">
                          {t('document', 'Document')}
                        </option>
                        <option value="webpage">
                          {t('webpage', 'Webpage')}
                        </option>
                        <option value="manual">
                          {t('manual_entry', 'Manual Entry')}
                        </option>
                        <option value="training">
                          {t('training_data', 'Training Data')}
                        </option>
                        <option value="faq">{t('faq', 'FAQ')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="label-text label">
                        {t('source_url', 'Source URL')}
                      </label>
                      <input
                        type="url"
                        value={metadata.source_url}
                        onChange={(e) =>
                          handleMetadataChange('source_url', e.target.value)
                        }
                        className="input-sm input w-full"
                        placeholder={t(
                          'source_url_placeholder',
                          'https://example.com/source',
                        )}
                      />
                    </div>

                    <div>
                      <label className="label-text label">
                        {t('source', 'Source')}
                      </label>
                      <input
                        type="text"
                        value={metadata.source}
                        onChange={(e) =>
                          handleMetadataChange('source', e.target.value)
                        }
                        className="input-sm input w-full"
                        placeholder={t(
                          'source_placeholder',
                          'Source identifier',
                        )}
                      />
                    </div>
                  </div>

                  {/* Help Section */}
                  <div className="space-y-2">
                    <h3 className="text-base-content text-sm font-medium">
                      {t('help', 'Help')}
                    </h3>
                    <div className="text-base-content/70 space-y-1 text-xs">
                      <p>
                        •{' '}
                        {t(
                          'help_voice_input',
                          'Use voice input with the microphone button',
                        )}
                      </p>
                      <p>
                        •{' '}
                        {t(
                          'help_formatting',
                          'Rich text formatting with the toolbar',
                        )}
                      </p>
                      <p>
                        • {t('help_tags', 'Add tags to organize your memories')}
                      </p>
                      <p>
                        •{' '}
                        {t(
                          'help_source_info',
                          'Include source information for reference',
                        )}
                      </p>
                      <p>
                        •{' '}
                        {t(
                          'help_media',
                          'Upload media files or select from library',
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Media Components */}
      {showMediaLibrary && (
        <MediaLibrary
          agentId={agentId}
          onSelectMedia={handleMediaSelect}
          isOpen={showMediaLibrary}
          onClose={() => setShowMediaLibrary(false)}
        />
      )}
    </div>
  );
};

export default MemoryEditorDialog;
